/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

// Single source of truth for location access. Platform-routed:
//
//   • iOS native (Capacitor.getPlatform() === 'ios')
//       → WKWebView's built-in navigator.geolocation
//       The @capacitor/geolocation 8.1.0 plugin is bundled but its JS-to-
//       native bridge silently drops messages on iOS 26 / Xcode 26.4.1 /
//       Capacitor 8.3.1. Confirmed via NSLog probe: GeolocationPlugin.load()
//       runs cleanly in 2ms but checkPermissions/requestPermissions/
//       getCurrentPosition are never invoked natively, even though
//       Capacitor.isPluginAvailable('Geolocation') returns true and other
//       plugins (App, AppleSignIn) bridge fine through the same bridge.
//       Eight hypotheses ruled out: build state, version skew, missing
//       packageClassList, duplicate cores, Swift module realization,
//       IONGLOCManagerWrapper hangs, @capacitor/synapse interference,
//       newer plugin versions (8.2.0's Swift is byte-identical to 8.1.0).
//       WKWebView's navigator.geolocation does NOT use the Capacitor bridge
//       and works correctly under iOS 26.
//
//   • Android native (Capacitor.getPlatform() === 'android')
//       → @capacitor/geolocation Capacitor plugin
//       The Android bridge is not affected by this bug.
//
//   • Web (Capacitor.getPlatform() === 'web')
//       → navigator.geolocation
//       Browsers handle permission contextually on first getCurrentPosition.
//
// Permission priming (ensureNativeLocationPermission) is platform-routed
// to match: on iOS we trigger the WKWebView dialog with a one-shot
// navigator.geolocation.getCurrentPosition call; on Android we use the
// Capacitor plugin's checkPermissions/requestPermissions; on web it's a
// no-op (browser prompts on first real call).

import { Capacitor } from "@capacitor/core";

export interface SafePosition {
  coords: { latitude: number; longitude: number };
}

export interface SafePositionError {
  code: number;
  message: string;
}

export interface SafePositionOptions {
  enableHighAccuracy?: boolean;
  timeout?: number;
  maximumAge?: number;
}

type Platform = "ios" | "android" | "web";

const platform = (): Platform => {
  const p = Capacitor.getPlatform();
  if (p === "ios" || p === "android") return p;
  return "web";
};

async function loadCapacitorGeolocation() {
  // Lazy-load so the web bundle never pulls the native bridge in. Used by
  // the Android path only — iOS native and web both go through
  // navigator.geolocation.
  //
  // IMPORTANT: wrap the plugin in a plain object instead of returning it
  // bare. `Geolocation` is a Capacitor `registerPlugin` Proxy that traps
  // EVERY property access — including `.then`. If this async function (or any
  // promise) resolves with the bare proxy, the JS promise-resolution
  // procedure probes `value.then`, hits the proxy trap, and throws
  // `"Geolocation.then()" is not implemented on android`, so the location
  // call never completes → useGeolocation times out → map falls back to
  // US center (Kansas). Returning { Geolocation } keeps the resolution value
  // non-thenable.
  const mod = await import("@capacitor/geolocation");
  return { Geolocation: mod.Geolocation };
}

// ── navigator.geolocation helpers (iOS native + web) ────────────────────────

function getCurrentPositionViaWebApi(
  options?: { timeout?: number },
): Promise<{ lat: number; lng: number }> {
  return new Promise((resolve, reject) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      reject(new Error("Geolocation not supported"));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      (err) => reject(err),
      { enableHighAccuracy: true, timeout: options?.timeout ?? 10000, maximumAge: 60000 },
    );
  });
}

function watchPositionViaWebApi(
  onSuccess: (pos: SafePosition) => void,
  onError: (err: SafePositionError) => void,
  options?: SafePositionOptions,
): WatchHandle {
  if (typeof navigator === "undefined" || !navigator.geolocation) {
    onError({ code: 2, message: "Geolocation not supported" });
    return { clear: () => {} };
  }
  const id = navigator.geolocation.watchPosition(
    (pos) =>
      onSuccess({
        coords: { latitude: pos.coords.latitude, longitude: pos.coords.longitude },
      }),
    (err) => onError({ code: err.code, message: err.message }),
    options,
  );
  return { clear: () => navigator.geolocation.clearWatch(id) };
}

// ── Permission priming ──────────────────────────────────────────────────────

// Concurrent callers (Index priming + useGeolocation's tryFresh + watch all
// fire on the same render) share a single in-flight promise so iOS only sees
// one permission prompt. Cleared in finally so subsequent independent calls
// re-run the check.
let inflightPermissionRequest: Promise<void> | null = null;

// Hard ceiling for the permission flow. Even with the navigator.geolocation
// workaround, we keep an outer timeout so the post-auth loader gate in
// Index.tsx can never hang forever — protects against future regressions
// in either the WKWebView geolocation API or the Capacitor Android bridge.
// useGeolocation has its own hard timeout that falls back to cached/US-center.
const PERMISSION_FLOW_TIMEOUT_MS = 8000;

export function ensureNativeLocationPermission(): Promise<void> {
  if (platform() === "web") return Promise.resolve();
  if (inflightPermissionRequest) return inflightPermissionRequest;
  const flow = runPermissionFlow();
  // If the timeout wins the race, runPermissionFlow may still reject later
  // when the underlying API eventually responds. Attach a no-op handler so
  // delayed rejection doesn't surface as an unhandled promise rejection.
  flow.catch(() => {});
  const timeout = new Promise<void>((resolve) =>
    setTimeout(() => {
      console.warn(
        "[geolocation] ensureNativeLocationPermission timed out after",
        PERMISSION_FLOW_TIMEOUT_MS,
        "ms; UI will fall through",
      );
      resolve();
    }, PERMISSION_FLOW_TIMEOUT_MS),
  );
  const promise = Promise.race([flow, timeout]).finally(() => {
    inflightPermissionRequest = null;
  });
  inflightPermissionRequest = promise;
  return promise;
}

async function runPermissionFlow(): Promise<void> {
  const p = platform();

  if (p === "ios") {
    // WKWebView's navigator.geolocation prompts iOS the first time
    // getCurrentPosition or watchPosition is called. There is no separate
    // requestPermissions API. Trigger the prompt with a throwaway call and
    // resolve regardless of grant/deny — useGeolocation will read the
    // resolved permission state on its next real call.
    return new Promise<void>((resolve) => {
      if (typeof navigator === "undefined" || !navigator.geolocation) {
        resolve();
        return;
      }
      navigator.geolocation.getCurrentPosition(
        () => resolve(), // granted
        () => resolve(), // denied (or unavailable) — UI takes over from there
        // Lower accuracy + short timeout: we only care about provoking the
        // dialog, not actually getting a fix here. The real hooks will fetch
        // a fresh fix once permission is resolved.
        { enableHighAccuracy: false, timeout: 5000, maximumAge: 60000 },
      );
    });
  }

  // Android — existing Capacitor plugin flow.
  const { Geolocation } = await loadCapacitorGeolocation();
  let status;
  try {
    status = await Geolocation.checkPermissions();
  } catch (err) {
    // If checkPermissions itself fails, fall through and let
    // getCurrentPosition surface the error.
    console.warn("[geolocation] checkPermissions failed", err);
    return;
  }
  if (status.location === "granted" || status.coarseLocation === "granted") {
    return;
  }
  if (status.location === "denied") {
    const e = new Error(
      "Location permission denied. Enable it in Settings > MyAmbits > Location.",
    ) as Error & { code: number };
    e.code = 1; // PERMISSION_DENIED
    throw e;
  }
  // prompt / prompt-with-rationale → ask now
  try {
    const requested = await Geolocation.requestPermissions({
      permissions: ["location"],
    });
    if (requested.location !== "granted" && requested.coarseLocation !== "granted") {
      const e = new Error(
        "Location permission denied. Enable it in Settings > MyAmbits > Location.",
      ) as Error & { code: number };
      e.code = 1;
      throw e;
    }
  } catch (err) {
    if ((err as { code?: number })?.code === 1) throw err;
    console.warn("[geolocation] requestPermissions failed", err);
  }
}

// ── Public API: getCurrentPositionSafe / watchPositionSafe ──────────────────

export function getCurrentPositionSafe(
  options?: { timeout?: number },
): Promise<{ lat: number; lng: number }> {
  if (platform() === "android") {
    return loadCapacitorGeolocation().then(async ({ Geolocation }) => {
      await ensureNativeLocationPermission();
      const pos = await Geolocation.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: options?.timeout ?? 10000,
        maximumAge: 60000,
      });
      return { lat: pos.coords.latitude, lng: pos.coords.longitude };
    });
  }
  // iOS native + web → WKWebView/browser navigator.geolocation
  return getCurrentPositionViaWebApi(options);
}

export interface WatchHandle {
  clear: () => void;
}

export function watchPositionSafe(
  onSuccess: (pos: SafePosition) => void,
  onError: (err: SafePositionError) => void,
  options?: SafePositionOptions,
): WatchHandle {
  if (platform() === "android") {
    let cancelled = false;
    const watchIdPromise: Promise<string | null> = loadCapacitorGeolocation()
      .then(async ({ Geolocation }) => {
        await ensureNativeLocationPermission();
        return Geolocation.watchPosition(
          {
            enableHighAccuracy: options?.enableHighAccuracy ?? true,
            timeout: options?.timeout,
          },
          (pos, err) => {
            if (cancelled) return;
            if (err) {
              const code = (err as { code?: number }).code ?? 2;
              onError({ code, message: err.message ?? String(err) });
              return;
            }
            if (!pos) return;
            onSuccess({
              coords: {
                latitude: pos.coords.latitude,
                longitude: pos.coords.longitude,
              },
            });
          },
        );
      })
      .catch((err) => {
        if (!cancelled) {
          onError({ code: 2, message: (err as Error)?.message ?? "watchPosition failed" });
        }
        return null;
      });
    return {
      clear: () => {
        cancelled = true;
        void watchIdPromise.then(async (id) => {
          if (!id) return;
          try {
            const { Geolocation } = await loadCapacitorGeolocation();
            await Geolocation.clearWatch({ id });
          } catch {
            /* best-effort */
          }
        });
      },
    };
  }
  // iOS native + web → WKWebView/browser navigator.geolocation
  return watchPositionViaWebApi(onSuccess, onError, options);
}
